import { Link } from '@tanstack/react-router';
import { DoorOpen, Mail, Phone, UserRound } from 'lucide-react';
import { formatArea, labelOf, PROPERTY_ROLE_LABELS } from '@blimo/shared';
import { Badge, Button, Skeleton, initials } from '@blimo/ui';
import { unitTitle } from '../lib/units.js';
import { ItemList, ItemRow, Section } from './ui/section.jsx';

/** Object roles of the user at a property (auditor, meter_reader). @param {{ roles?: string[] }} props */
export function MyRoleBadges({ roles }) {
  if (!roles?.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((role) => (
        <Badge key={role} variant="secondary">
          {labelOf(PROPERTY_ROLE_LABELS, role)}
        </Badge>
      ))}
    </span>
  );
}

/**
 * My units as compact list rows (clickable).
 * @param {{ units?: any[], emptyText?: string, label?: string }} props
 */
export function UnitList({ units, emptyText = 'Ihnen sind in dieser Liegenschaft keine Einheiten zugeordnet.', label }) {
  if (!units?.length) return <p className="rounded-xl border bg-card px-4 py-3 text-[0.8125rem] text-muted-foreground">{emptyText}</p>;
  return (
    <ItemList aria-label={label}>
      {units.map((unit) => (
        <li key={unit.id}>
          <UnitRow unit={unit} />
        </li>
      ))}
    </ItemList>
  );
}

/** @param {{ unit: any }} props */
export function UnitRow({ unit }) {
  const meta = [unit.type, unit.living_space != null ? formatArea(unit.living_space) : null].filter(Boolean).join(' · ');
  return (
    <ItemRow
      as={Link}
      to="/immobilien/einheiten/$unitId"
      params={{ unitId: String(unit.id) }}
      icon={DoorOpen}
      title={unitTitle(unit)}
      subtitle={meta || undefined}
    />
  );
}

/**
 * Contact person of the property (from the API; null → explanation) as card.
 * @param {{ contact: { name?: string|null, email?: string|null, phone?: string|null } | null | undefined, title?: string, as?: 'h2'|'h3' }} props
 */
export function ContactCard({ contact, title = 'Ihr Ansprechpartner', as = 'h2' }) {
  return (
    <Section title={title} headingLevel={as} description="Ihre zuständige Verwaltung">
      {contact ? (
        <ContactDetails contact={contact} />
      ) : (
        <p className="text-[0.8125rem] text-muted-foreground">Für diese Liegenschaft ist noch kein Ansprechpartner hinterlegt.</p>
      )}
    </Section>
  );
}

/**
 * Verwalter-Organisation der Liegenschaft (F-124, Altportal cms_salepartner) als Karte.
 * @param {{ manager: { name?: string|null, email?: string|null, phone?: string|null }, as?: 'h2'|'h3' }} props
 */
export function ManagerCard({ manager, as = 'h2' }) {
  return (
    <Section title="Verwaltung" headingLevel={as} description="Verwalter der Liegenschaft">
      <ContactDetails contact={manager} role="Verwalter" />
    </Section>
  );
}

const telHref = (phone) => `tel:${phone.replace(/[^\d+]/g, '')}`;

/**
 * Contact: avatar, name, role, then phone / e-mail as links (icon + value) and small call/mail buttons.
 * @param {{ contact: { name?: string|null, email?: string|null, phone?: string|null }, role?: string, compact?: boolean }} props
 */
export function ContactDetails({ contact, role, compact = false }) {
  const who = contact.name || 'Ansprechpartner';
  return (
    <div className="@container">
      <div className="flex flex-col gap-2.5 @xl:flex-row @xl:items-center @xl:gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="portal-avatar size-9 text-xs" aria-hidden="true">
            {contact.name ? initials(contact.name) : <UserRound className="size-4" />}
          </span>
          <div className="flex min-w-0 flex-col">
            {contact.name && <p className="break-words text-sm font-medium leading-5">{contact.name}</p>}
            {role && <p className="text-xs text-muted-foreground">{role}</p>}
          </div>
        </div>
        {(contact.phone || contact.email) && (
          <div className="flex min-w-0 flex-col gap-0.5 pl-12 text-[0.8125rem] @xl:pl-0">
            {contact.phone && (
              <a href={telHref(contact.phone)} className="inline-flex min-h-7 items-center gap-2 text-foreground hover:text-(--primary-text)">
                <Phone className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="tabular-nums">{contact.phone}</span>
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="inline-flex min-h-7 min-w-0 items-center gap-2 text-foreground hover:text-(--primary-text)">
                <Mail className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="break-all">{contact.email}</span>
              </a>
            )}
          </div>
        )}
        {!compact && (contact.phone || contact.email) && (
          <div className="hidden shrink-0 gap-1.5 @xl:flex">
            {contact.phone && (
              <Button asChild variant="outline" size="icon-sm">
                <a href={telHref(contact.phone)} aria-label={`${who} anrufen`} title="Anrufen">
                  <Phone aria-hidden="true" />
                </a>
              </Button>
            )}
            {contact.email && (
              <Button asChild variant="outline" size="icon-sm">
                <a href={`mailto:${contact.email}`} aria-label={`E-Mail an ${who}`} title="E-Mail schreiben">
                  <Mail aria-hidden="true" />
                </a>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CardsSkeleton({ count = 2 }) {
  return (
    <div className="grid gap-3 md:grid-cols-2" aria-busy="true" aria-label="Wird geladen">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className="h-48 w-full rounded-xl!" />
      ))}
    </div>
  );
}
